import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, ClipboardCheck, History, PackagePlus, RefreshCw, RotateCcw, Search, UploadCloud, X } from "lucide-react";
import {
  api,
  can,
  type ApiUser,
  type OrderProgressBatch,
  type OrderProgressPage as OrderProgressPageData,
  type OrderProgressRow,
} from "./appApi";
import OrderProgressImportModal from "./OrderProgressImportModal";
import { OrderShipmentEntryModal } from "./logistics";

type Props = { user: ApiUser; toast: (message: string) => void };

const stages = [
  { key: "photographed", label: "拍摄", permission: "order.progress.shooting.edit" },
  { key: "retouched", label: "修图", permission: "order.progress.retouch.edit" },
  { key: "layout_completed", label: "排版", permission: "order.progress.layout.edit" },
  { key: "factory_sent", label: "送厂", permission: "order.progress.factory.edit" },
  { key: "production_completed", label: "制作", permission: "order.progress.factory.edit" },
] as const;

function shortDate(value?: string | null) {
  if (!value) return "-";
  return value.replace("T", " ").slice(0, 16);
}

function ProgressToggle({
  row,
  stage,
  allowed,
  busy,
  onToggle,
}: {
  row: OrderProgressRow;
  stage: typeof stages[number];
  allowed: boolean;
  busy: boolean;
  onToggle: () => void;
}) {
  const item = row.progress.stages[stage.key];
  return (
    <button
      className={`production-stage-toggle ${item?.completed ? "done" : "pending"} ${allowed ? "editable" : "readonly"}`}
      disabled={!allowed || busy}
      onClick={onToggle}
      title={allowed ? `点击设置为${item?.completed ? "未完成" : "已完成"}` : "当前岗位只能查看"}
    >
      <i>{item?.completed ? <Check size={13} /> : ""}</i>
      <span>{item?.text || `未${stage.label}`}</span>
      {item?.automatic && <small>系统</small>}
    </button>
  );
}

export default function OrderProgressPage({ user, toast }: Props) {
  const [filters, setFilters] = useState({
    keyword: "",
    brand: "",
    campus: "",
    stage: "",
    completed: "",
    retouch_overdue_days: "",
    layout_overdue_days: "",
  });
  const [keywordInput, setKeywordInput] = useState("");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [data, setData] = useState<OrderProgressPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [busyKey, setBusyKey] = useState("");
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [logs, setLogs] = useState<any[] | null>(null);
  const [logOrder, setLogOrder] = useState<OrderProgressRow | null>(null);
  const [showImport, setShowImport] = useState(false);
  const [shipmentOrder, setShipmentOrder] = useState<OrderProgressRow | null>(null);
  const [showBatches, setShowBatches] = useState(false);
  const [batches, setBatches] = useState<OrderProgressBatch[] | null>(null);
  const [undoingBatch, setUndoingBatch] = useState("");

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      setData(await api.orderProgressPage({ ...filters, page, per_page: perPage }));
    } catch (error: any) {
      const message = error.message || "订单进度加载失败";
      setData(null);
      setLoadError(message);
      toast(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [JSON.stringify(filters), page, perPage]);

  const rows = data?.items || [];
  const selectedIds = useMemo(() => Object.entries(selected).filter(([, checked]) => checked).map(([id]) => Number(id)), [selected]);
  const allSelected = rows.length > 0 && rows.every((row) => selected[String(row.id)]);
  const capabilities = data?.capabilities || {};
  const canManageShipments = can(user, "logistics.manage");

  const setFilter = (key: keyof typeof filters, value: string) => {
    setPage(1);
    setSelected({});
    setFilters((current) => {
      const next = { ...current, [key]: value };
      if ((key === "stage" || key === "completed") && value) {
        next.retouch_overdue_days = "";
        next.layout_overdue_days = "";
      }
      return next;
    });
  };

  const setOverdueFilter = (key: "retouch_overdue_days" | "layout_overdue_days", value: string) => {
    setPage(1);
    setSelected({});
    setFilters((current) => ({
      ...current,
      stage: "",
      completed: "",
      retouch_overdue_days: key === "retouch_overdue_days" ? value : "",
      layout_overdue_days: key === "layout_overdue_days" ? value : "",
    }));
  };

  const submitSearch = () => setFilter("keyword", keywordInput.trim());

  const reset = () => {
    setKeywordInput("");
    setPage(1);
    setSelected({});
    setFilters({ keyword: "", brand: "", campus: "", stage: "", completed: "", retouch_overdue_days: "", layout_overdue_days: "" });
  };

  const toggleStage = async (row: OrderProgressRow, stage: typeof stages[number]) => {
    const current = Boolean(row.progress.stages[stage.key]?.completed);
    const stageIndex = stages.findIndex((item) => item.key === stage.key);
    const missingUpstream = stages.slice(0, Math.max(stageIndex, 0)).filter(
      (item) => !row.progress.stages[item.key]?.completed,
    );
    let reason = "";
    if (current) {
      const value = window.prompt(`取消“${row.child_name || row.report_no}”的${stage.label}完成状态，请填写原因：`, "");
      if (value === null) return;
      reason = value.trim();
      if (!reason) {
        toast("取消完成状态必须填写原因");
        return;
      }
    } else if (!window.confirm(
      `确认“${row.child_name || row.report_no}”已经完成${stage.label}吗？${missingUpstream.length ? `\n系统将同步补齐：${missingUpstream.map((item) => item.label).join("、")}` : ""}`,
    )) {
      return;
    }
    const key = `${row.id}:${stage.key}`;
    setBusyKey(key);
    try {
      await api.updateOrderProgressStage(row.id, stage.key, !current, reason);
      toast(`${stage.label}进度已更新${missingUpstream.length ? `，已同步补齐${missingUpstream.map((item) => item.label).join("、")}` : ""}`);
      await load();
    } catch (error: any) {
      toast(error.message || "进度更新失败");
    } finally {
      setBusyKey("");
    }
  };

  const batchUpdate = async (stageKey: string, completed: boolean) => {
    if (!selectedIds.length) {
      toast("请先勾选需要更新的订单");
      return;
    }
    const stage = stages.find((item) => item.key === stageKey);
    if (!stage) return;
    let reason = "";
    if (!completed) {
      const value = window.prompt(`批量取消${stage.label}状态，请填写原因：`, "");
      if (value === null) return;
      reason = value.trim();
      if (!reason) {
        toast("批量取消必须填写原因");
        return;
      }
    } else if (!window.confirm(`确认将选中的 ${selectedIds.length} 笔订单设置为“已${stage.label}”吗？\n系统会自动补齐每笔订单缺少的前置进度。`)) {
      return;
    }
    setBusyKey(`batch:${stageKey}`);
    try {
      const result = await api.batchUpdateOrderProgress(selectedIds, stageKey, completed, reason);
      const skipped = result.skipped?.length || 0;
      toast(`成功更新 ${result.updated} 笔${result.cascade_updated ? `，补齐 ${result.cascade_updated} 个前置进度` : ""}${skipped ? `，跳过 ${skipped} 笔` : ""}`);
      setSelected({});
      await load();
    } catch (error: any) {
      toast(error.message || "批量更新失败");
    } finally {
      setBusyKey("");
    }
  };

  const openLogs = async (row: OrderProgressRow) => {
    setLogOrder(row);
    setLogs(null);
    try {
      setLogs(await api.orderProgressLogs(row.id));
    } catch (error: any) {
      toast(error.message || "操作记录加载失败");
      setLogs([]);
    }
  };

  const loadBatches = async () => {
    setShowBatches(true);
    setBatches(null);
    try {
      setBatches(await api.orderProgressBatches());
    } catch (error: any) {
      toast(error.message || "批量操作记录加载失败");
      setBatches([]);
    }
  };

  const undoBatch = async (batch: OrderProgressBatch) => {
    const action = batch.completed ? `设为已${batch.stage_name}` : `取消${batch.stage_name}`;
    if (!window.confirm(
      `确认撤回这个批次吗？\n\n${action} · ${batch.updated_count} 笔订单\n批次号：${batch.batch_id}\n\n批次后又被修改过的订单会自动跳过。`,
    )) return;
    const value = window.prompt("请填写撤回原因（必填）：", "批量操作有误");
    if (value === null) return;
    const reason = value.trim();
    if (!reason) {
      toast("撤回批次必须填写原因");
      return;
    }
    setUndoingBatch(batch.batch_id);
    try {
      const result = await api.undoOrderProgressBatch(batch.batch_id, reason);
      const skippedOrders = new Set((result.skipped || []).map((item) => String(item.order_id))).size;
      toast(`已撤回 ${result.restored_orders} 笔订单${skippedOrders ? `，保护性跳过 ${skippedOrders} 笔` : ""}`);
      setBatches(await api.orderProgressBatches());
      setSelected({});
      await load();
    } catch (error: any) {
      toast(error.message || "批次撤回失败");
    } finally {
      setUndoingBatch("");
    }
  };

  const stats = data?.stats;
  const summaryCards = [
    { label: "全部订单", value: stats?.total || 0, stage: "", completed: "" },
    { label: "待拍摄", value: stats?.pending_photographed || 0, stage: "photographed", completed: "false" },
    { label: "待修图", value: stats?.pending_retouched || 0, stage: "retouched", completed: "false" },
    { label: "待排版", value: stats?.pending_layout || 0, stage: "layout_completed", completed: "false" },
    { label: "待制作", value: stats?.pending_production || 0, stage: "production_completed", completed: "false" },
    { label: "待发货", value: stats?.pending_shipped || 0, stage: "shipped", completed: "false" },
  ];
  const overdueOptions = [
    { value: "7", label: "结营满7天" },
    { value: "15", label: "结营满15天" },
    { value: "20", label: "结营满20天" },
    { value: "30", label: "结营满30天（1个月）" },
  ];

  return (
    <>
      <div className="production-page-head">
        <div><span>ORDER PRODUCTION</span><h1>订单进度</h1><p>按拍摄、修图、排版、送厂、制作和发货跟进交付进度。</p></div>
        <div className="production-page-actions">
          {capabilities.batch && stages.some(stage => capabilities[stage.key]) && <button className="primary" onClick={() => setShowImport(true)}><UploadCloud size={16} />批量导入进度</button>}
          {capabilities.batch && <button className="secondary" onClick={loadBatches}><RotateCcw size={16} />批次记录/撤回</button>}
          <button className="secondary" onClick={load}><RefreshCw size={16} />刷新</button>
        </div>
      </div>

      <div className="production-summary-grid">
        {summaryCards.map((card) => (
          <button key={card.label} className={filters.stage === card.stage && filters.completed === card.completed ? "active" : ""} onClick={() => {
            setPage(1);
            setSelected({});
            setFilters((current) => ({ ...current, stage: card.stage, completed: card.completed, retouch_overdue_days: "", layout_overdue_days: "" }));
          }}>
            <span>{card.label}</span><b>{card.value}</b><small>点击筛选</small>
          </button>
        ))}
      </div>

      <div className="production-overdue-summary">
        <div className="production-overdue-intro"><i><AlertTriangle size={18} /></i><span><b>结营交付预警</b><small>按北京时间统计，自动排除全额退款订单</small></span></div>
        <button className={`retouch ${filters.retouch_overdue_days === "15" ? "active" : ""}`} onClick={() => setOverdueFilter("retouch_overdue_days", filters.retouch_overdue_days === "15" ? "" : "15")}>
          <span>结营15天未修图</span><b>{stats?.ended_15d_unretouched || 0}</b><small>{filters.retouch_overdue_days === "15" ? "筛选中 · 点击取消" : "点击查看逾期订单"}</small>
        </button>
        <button className={`layout ${filters.layout_overdue_days === "15" ? "active" : ""}`} onClick={() => setOverdueFilter("layout_overdue_days", filters.layout_overdue_days === "15" ? "" : "15")}>
          <span>结营15天未排版</span><b>{stats?.ended_15d_unlayout || 0}</b><small>{filters.layout_overdue_days === "15" ? "筛选中 · 点击取消" : "点击查看逾期订单"}</small>
        </button>
      </div>

      <div className="production-filter-panel">
        <div className="production-filter-row production-filter-primary-row">
          <label className="production-search"><Search size={17} /><input value={keywordInput} onChange={(event) => setKeywordInput(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") submitSearch(); }} placeholder="订单号、孩子姓名、班级、款式" /><button onClick={submitSearch}>查询</button></label>
          <label className="production-filter-field"><span>品牌</span><select value={filters.brand} onChange={(event) => setFilter("brand", event.target.value)}><option value="">全部品牌</option>{(data?.options.brands || []).map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          <label className="production-filter-field"><span>校区</span><select value={filters.campus} onChange={(event) => setFilter("campus", event.target.value)}><option value="">全部校区</option>{(data?.options.campuses || []).map((item) => <option key={item} value={item}>{item}</option>)}</select></label>
          <button className="secondary production-reset-btn" onClick={reset}>重置</button>
        </div>
        <div className="production-filter-row production-filter-warning-row">
          <label className="production-filter-field"><span>进度阶段</span><select value={filters.stage} onChange={(event) => setFilter("stage", event.target.value)}><option value="">全部阶段</option>{stages.map((item) => <option key={item.key} value={item.key}>{item.label}</option>)}<option value="shipped">发货</option></select></label>
          <label className="production-filter-field"><span>完成状态</span><select value={filters.completed} onChange={(event) => setFilter("completed", event.target.value)}><option value="">全部状态</option><option value="true">已完成</option><option value="false">未完成</option></select></label>
          <label className={`production-filter-field overdue-field retouch ${filters.retouch_overdue_days ? "active" : ""}`}><span>结营未修图</span><select value={filters.retouch_overdue_days} onChange={(event) => setOverdueFilter("retouch_overdue_days", event.target.value)}><option value="">不限结营天数</option>{overdueOptions.map(item => <option key={item.value} value={item.value}>{item.label}未修图</option>)}</select></label>
          <label className={`production-filter-field overdue-field layout ${filters.layout_overdue_days ? "active" : ""}`}><span>结营未排版</span><select value={filters.layout_overdue_days} onChange={(event) => setOverdueFilter("layout_overdue_days", event.target.value)}><option value="">不限结营天数</option>{overdueOptions.map(item => <option key={item.value} value={item.value}>{item.label}未排版</option>)}</select></label>
        </div>
      </div>

      {selectedIds.length > 0 && capabilities.batch && (
        <div className="production-batch-bar">
          <b>已选择 {selectedIds.length} 笔</b>
          {stages.map((stage) => capabilities[stage.key] && <span key={stage.key}><button onClick={() => batchUpdate(stage.key, true)}>设为已{stage.label}</button><button className="muted" onClick={() => batchUpdate(stage.key, false)}>取消{stage.label}</button></span>)}
        </div>
      )}

      <div className="production-table-card">
        {loading ? <div className="production-loading">正在加载订单进度…</div> : loadError ? (
          <div className="production-load-error">
            <ClipboardCheck size={30} />
            <b>订单进度接口加载失败</b>
            <p>{loadError}</p>
            <small>请确认两张订单进度数据表已导入后端正在使用的数据库，并且后端服务已经重启。</small>
            <button className="secondary" onClick={load}><RefreshCw size={15} />重新加载</button>
          </div>
        ) : rows.length ? (
          <table>
            <thead><tr><th><input type="checkbox" checked={allSelected} onChange={() => setSelected((current) => { const next = { ...current }; rows.forEach((row) => { next[String(row.id)] = !allSelected; }); return next; })} /></th><th>订单/孩子</th><th>品牌/班级/款式</th>{stages.map((stage) => <th key={stage.key}>{stage.label}</th>)}<th>发货</th><th>更新</th><th>记录</th></tr></thead>
            <tbody>{rows.map((row) => (
              <tr key={row.id}>
                <td><input type="checkbox" checked={Boolean(selected[String(row.id)])} onChange={(event) => setSelected((current) => ({ ...current, [String(row.id)]: event.target.checked }))} /></td>
                <td className="production-order-cell"><b>{row.child_name || "未填姓名"}</b><span>{row.report_no || row.id}</span><small>{shortDate(row.created_at)}</small>{row.end_date && <small className="production-end-date">结营：{row.end_date}</small>}{filters.retouch_overdue_days && <em className="production-overdue-badge retouch">已结营 {row.days_since_end || 0} 天未修图</em>}{filters.layout_overdue_days && <em className="production-overdue-badge layout">已结营 {row.days_since_end || 0} 天未排版</em>}</td>
                <td className="production-info-cell"><b>{row.brand || "未填品牌"}</b><span>{row.campus || "未填校区"} / {row.class_name || "未填班级"}</span><small>{row.package_name || "未填款式"}</small></td>
                {stages.map((stage) => <td key={stage.key}><ProgressToggle row={row} stage={stage} allowed={Boolean(capabilities[stage.key] && can(user, stage.permission))} busy={busyKey === `${row.id}:${stage.key}`} onToggle={() => toggleStage(row, stage)} /></td>)}
                <td><div className="production-shipping-cell"><div className={`production-shipping-state ${row.progress.stages.shipped?.completed ? "done" : ""}`}><i>{row.progress.stages.shipped?.completed ? <Check size={13} /> : ""}</i><span>{row.progress.stages.shipped?.text}</span><small>{row.progress.stages.shipped?.shipment_count || 0} 个包裹</small></div>{canManageShipments && <button className="production-shipment-entry-btn" onClick={() => setShipmentOrder(row)}><PackagePlus size={14} />录入单号</button>}</div></td>
                <td className="production-update-cell"><b>{row.progress.updated_by_name || "系统状态"}</b><span>{shortDate(row.progress.updated_at)}</span></td>
                <td><button className="production-log-btn" onClick={() => openLogs(row)}><History size={15} />记录</button></td>
              </tr>
            ))}</tbody>
          </table>
        ) : <div className="production-empty"><ClipboardCheck size={28} /><b>暂无符合条件的订单</b></div>}
      </div>

      <div className="production-pagination">
        <span>共 {data?.total || 0} 笔</span>
        <select value={perPage} onChange={(event) => { setPerPage(Number(event.target.value)); setPage(1); }}><option value="30">30条/页</option><option value="50">50条/页</option><option value="100">100条/页</option></select>
        <button disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>上一页</button>
        <b>{page} / {data?.pages || 1}</b>
        <button disabled={page >= (data?.pages || 1)} onClick={() => setPage((value) => value + 1)}>下一页</button>
      </div>

      {logOrder && (
        <div className="modal-backdrop">
          <div className="modal production-log-modal">
            <div className="modal-head"><div><span className="eyebrow">PROGRESS LOG</span><h2>进度操作记录</h2><p>{logOrder.child_name || logOrder.report_no}</p></div><button className="icon-btn" onClick={() => { setLogOrder(null); setLogs(null); }}><X size={18} /></button></div>
            <div className="production-log-list">
              {logs === null ? <div className="production-loading">加载记录中…</div> : logs.length ? logs.map((log) => <article key={log.id}><i className={log.new_completed ? "done" : "cancel"}>{log.new_completed ? "✓" : "×"}</i><div><b>{log.stage_name}：{log.new_completed ? "已完成" : "已取消"}</b><span>{log.operator_name || "未知操作人"} · {shortDate(log.created_at)}</span>{log.reason && <p>{log.reason}</p>}</div></article>) : <div className="production-empty">暂无人工操作记录</div>}
            </div>
          </div>
        </div>
      )}
      {showBatches && (
        <div className="modal-backdrop">
          <div className="modal production-batch-history-modal">
            <div className="modal-head">
              <div><span className="eyebrow">SAFE UNDO</span><h2>批量操作记录</h2><p>按批次精确恢复；批次后有新修改的订单会自动跳过。</p></div>
              <button className="icon-btn" onClick={() => setShowBatches(false)}><X size={18} /></button>
            </div>
            <div className="production-batch-safety-note"><AlertTriangle size={17} /><span><b>撤回不会删除订单</b><small>系统只恢复该批次实际改动的进度，并保留完整操作记录。</small></span></div>
            <div className="production-batch-history-list">
              {batches === null ? <div className="production-loading">正在加载批次记录…</div> : batches.length ? batches.map((batch) => (
                <article key={batch.batch_id} className={batch.undone ? "undone" : ""}>
                  <div className="production-batch-history-main">
                    <span className={`production-batch-source ${batch.source}`}>{batch.source === "import" ? "名单导入" : "勾选批量"}</span>
                    <div><b>{batch.completed ? `设为已${batch.stage_name}` : `取消${batch.stage_name}`}</b><small>{batch.operator_name} · {shortDate(batch.created_at)}</small></div>
                  </div>
                  <div className="production-batch-history-count"><b>{batch.updated_count}</b><span>笔订单</span>{batch.cascade_update_count > 0 && <small>含补齐 {batch.cascade_update_count} 个前置进度</small>}</div>
                  <code>{batch.batch_id}</code>
                  <div className="production-batch-history-action">
                    {batch.undone ? <span className="production-batch-undone">已由 {batch.undone_by_name || "管理员"} 撤回<br /><small>{shortDate(batch.undone_at)}</small></span> : batch.can_undo && batch.updated_count > 0 ? (
                      <button disabled={Boolean(undoingBatch)} onClick={() => undoBatch(batch)}><RotateCcw size={15} />{undoingBatch === batch.batch_id ? "撤回中…" : "撤回此批次"}</button>
                    ) : <span className="production-batch-unavailable">不可撤回</span>}
                  </div>
                </article>
              )) : <div className="production-empty"><History size={26} /><b>暂无可识别的批量操作</b><span>后续名单导入和勾选批量修改都会记录在这里。</span></div>}
            </div>
          </div>
        </div>
      )}
      {showImport && <OrderProgressImportModal
        capabilities={capabilities}
        brands={data?.options.brands || []}
        campuses={data?.options.campuses || []}
        close={() => setShowImport(false)}
        toast={toast}
        done={async message => { setShowImport(false); toast(message); await load(); }}
      />}
      {shipmentOrder && <OrderShipmentEntryModal
        orderId={shipmentOrder.id}
        childName={shipmentOrder.child_name || shipmentOrder.report_no}
        close={() => setShipmentOrder(null)}
        toast={toast}
        saved={async () => { await load(); }}
      />}
    </>
  );
}
